import {
  type BudgetAlert,
  type BudgetConfig,
  type CurrencyCode,
  TimeInterval,
  TimeSettingsMode,
} from "@doitintl/cmp-models";
import { type ModelTimestamp } from "@doitintl/models-any";
import { darken, lighten } from "@mui/material";
import { type Theme } from "@mui/material/styles";
import { type CallbackDataParams } from "echarts/types/dist/shared";
import { type EChartsInstance } from "echarts-for-react";
import capitalize from "lodash/capitalize";
import { DateTime } from "luxon";

import { generateHighchartList3 } from "../../../../src/cmpBaseColors";
import { budgetTxt } from "../../../assets/texts/CloudAnalytics/budget";
import { formatValueWithCurrency } from "../../../Components/hooks/useFormatter";
import { ThemeModes } from "../../../muiThemeTypes";
import { type BudgetForecastRowsData } from "../../../types/Budget";
import { type MarkPointData } from "../renderers/useChartsSeries";
import { BudgetConfigurations, BudgetDynamicConfigurations, BudgetTypes, type TimeRangeOption } from "../utilities";
import { type BudgetChartData } from "./budgetViewTypes";
import type { Budget, BudgetRow, BudgetRows } from "../../../types";

export const fullPreviewAmountByInterval = {
  [TimeInterval.DAY]: 24,
  [TimeInterval.WEEK]: 7,
  [TimeInterval.MONTH]: daysInCurrentMonth(),
  [TimeInterval.QUARTER]: weeksInCurrentQuarter(),
  [TimeInterval.YEAR]: 12,
};

export const previewAmountByInterval = {
  [TimeInterval.DAY]: hoursElapsedSinceStartOfDay(),
  [TimeInterval.WEEK]: daysElapsedSinceStartOfWeek(),
  [TimeInterval.MONTH]: daysElapsedSinceStartOfMonth(),
  [TimeInterval.QUARTER]: weeksElapsedSinceStartOfQuarter(),
  [TimeInterval.YEAR]: monthsElapsedSinceStartOfYear(),
};

const getRecurringTimeUnitAmount = (timeInterval: TimeInterval) => {
  switch (timeInterval) {
    case TimeInterval.DAY:
      return 30;
    case TimeInterval.WEEK:
      return 16;
    case TimeInterval.MONTH:
      return 6;
    case TimeInterval.QUARTER:
      return 4;
    case TimeInterval.YEAR:
      return 2;
    default:
      return 1;
  }
};

export const getTimeRangeOption = (
  budgetType: BudgetTypes,
  timeInterval: TimeInterval,
  startPeriod: DateTime,
  endPeriod: DateTime
): TimeRangeOption => {
  const amount = budgetType === BudgetTypes.RECURRING ? getRecurringTimeUnitAmount(timeInterval) : undefined;

  const range =
    budgetType === BudgetTypes.FIXED
      ? {
          start: startPeriod,
          end: endPeriod,
        }
      : undefined;

  const mode = budgetType === BudgetTypes.FIXED ? TimeSettingsMode.Fixed : TimeSettingsMode.Last;

  const includeCurrent = budgetType === BudgetTypes.RECURRING;

  return {
    mode,
    time: timeInterval,
    amount,
    range,
    includeCurrent,
  };
};

export const getFixedTimeInterval = (startPeriod: DateTime, endPeriod: DateTime): TimeInterval => {
  const diff = endPeriod.diff(startPeriod, "months").months;
  if (diff >= 12) {
    return TimeInterval.YEAR;
  } else if (diff >= 6) {
    return TimeInterval.QUARTER;
  } else if (diff >= 2) {
    return TimeInterval.MONTH;
  } else if (diff >= 1) {
    return TimeInterval.WEEK;
  }
  return TimeInterval.DAY;
};

type budgetState = {
  shouldRefreshData: boolean;
  shouldRefreshPreview: boolean;
  refreshPreview: boolean;
};

type budgetStateAction = {
  type:
    | "shouldRefreshData"
    | "startedRefreshPreview"
    | "finishedRefreshPreview"
    | "shouldRefreshPreview"
    | "startedRefreshData";
  payload?: boolean;
};

export const budgetStateReducer = (state: budgetState, action: budgetStateAction): budgetState => {
  switch (action.type) {
    case "shouldRefreshPreview":
      return { ...state, shouldRefreshPreview: true };
    case "startedRefreshPreview":
      return { ...state, refreshPreview: true, shouldRefreshPreview: false };
    case "finishedRefreshPreview":
      return { ...state, refreshPreview: action.payload ?? false };
    case "shouldRefreshData":
      return { ...state, shouldRefreshData: true };
    case "startedRefreshData":
      return { ...state, shouldRefreshData: false };
  }
};

export function weeksInCurrentQuarter() {
  const now = new Date();
  const quarterStartMonth = Math.floor(now.getMonth() / 3) * 3;
  const quarterStart: any = new Date(now.getFullYear(), quarterStartMonth, 1);
  const quarterEnd: any = new Date(now.getFullYear(), quarterStartMonth + 3, 0);
  const weekMilliseconds = 7 * 24 * 60 * 60 * 1000;
  return Math.ceil((quarterEnd - quarterStart + 1) / weekMilliseconds);
}

export function daysInCurrentMonth() {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1;

  return new Date(year, month, 0).getDate();
}

export function makeDataCumulative(
  data: Array<{ y: number; [key: string]: any }>,
  startPoint = 0
): Array<{ y: number; [key: string]: any }> {
  let cumulativeSum = startPoint;
  return (
    data?.map((point) => {
      cumulativeSum += point.y;
      return {
        ...point,
        y: cumulativeSum,
      };
    }) || []
  );
}

export const formatTimeIntervalPhrase = (interval: TimeInterval) =>
  interval === TimeInterval.DAY ? "daily" : `${interval}ly`;

export const getFrequencyPhrase = (interval: TimeInterval, frequency: string, isFixedBudget: boolean) => {
  const intervalString = formatTimeIntervalPhrase(interval);
  return !isFixedBudget ? `${capitalize(intervalString)} ${frequency}` : frequency;
};

export const getNearestValue = (arr: number[] = [], val: number): number =>
  arr.reduce((p, n) => (Math.abs(p) > Math.abs(n - val) ? n - val : p), Infinity) + val;

export const buildRange = (
  startPeriod: DateTime,
  endPeriod: DateTime,
  timeInterval: TimeInterval,
  type: BudgetTypes
): DateTime[] => {
  const intervals = {
    [TimeInterval.DAY]: { hours: 1 },
    [TimeInterval.WEEK]: { days: 1 },
    [TimeInterval.MONTH]: { days: 1 },
    [TimeInterval.QUARTER]: { weeks: 1 },
    [TimeInterval.YEAR]: { months: 1 },
  };

  const startPeriodUTC = startPeriod.toUTC();
  const endPeriodUTC = endPeriod.toUTC();

  const range: DateTime[] = [];
  let current: DateTime = startPeriodUTC;

  let intervalToUse = intervals[timeInterval];

  if (type === BudgetTypes.FIXED && timeInterval === TimeInterval.MONTH) {
    const diffInMonths = endPeriodUTC.diff(startPeriodUTC, "months").months;

    if (diffInMonths >= 2) {
      intervalToUse = { months: 1 };
    }
  }

  while (current <= endPeriodUTC) {
    range.push(current);
    if (!intervalToUse) {
      throw new Error("Invalid time interval");
    }
    current = current.plus(intervalToUse).toUTC();
  }

  return range;
};

export const getFixedBudgetTimeRange = (budget: Budget, timeInterval: TimeInterval): DateTime[] => {
  if (!budget?.data?.config?.startPeriod || !budget?.data?.config?.endPeriod) {
    return [];
  }

  const fixedStartPeriod = DateTime.fromJSDate(budget.data.config.startPeriod.toDate());
  const fixedEndPeriod = DateTime.fromJSDate(budget.data.config.endPeriod.toDate());

  return buildRange(fixedStartPeriod, fixedEndPeriod, timeInterval, BudgetTypes.FIXED);
};

export const getFilteredDatesUpToToday = (timeRange: DateTime[]): number => {
  const todayUtc = DateTime.utc().startOf("day");

  return timeRange.filter((date) => date <= todayUtc).length;
};

export const fillNullValues = (chartData: number[], fullRangeData: string[], fromStart: boolean = false): number[] => {
  if (chartData.length === 0 && fullRangeData.length === 0) {
    return [];
  }

  const nullValuesArray = Array(fullRangeData.length - chartData.length).fill(null);
  const withNullValuesArray = fromStart ? nullValuesArray.concat(chartData) : chartData.concat(nullValuesArray);
  return withNullValuesArray;
};

export const getBudgetRowDate = (
  row: Array<number | null | string>,
  firstDateIdx: number,
  timeInterval: TimeInterval
): string => {
  const year = Number(row[firstDateIdx]);

  let rowDate: string;

  switch (timeInterval) {
    case TimeInterval.YEAR:
      rowDate = `${year}`;
      break;

    case TimeInterval.QUARTER:
    case TimeInterval.MONTH:
    case TimeInterval.WEEK: {
      const date = row[firstDateIdx + 1];
      rowDate = `${year}-${date}`;
      break;
    }

    case TimeInterval.DAY: {
      const month = row[firstDateIdx + 1];
      const day = row[firstDateIdx + 2];
      rowDate = `${year}-${month}-${day}`;
      break;
    }

    default:
      throw new Error("Invalid time interval");
  }

  return rowDate;
};

export const getRowMetricIndex = (
  firstDateIdx: number,
  dataTimeInterval: TimeInterval,
  utilizeAmortizedCost: boolean,
  isCSP: boolean
): number => {
  let metricIndex = firstDateIdx;

  // currently we only support MONTH and DAY dataTimeIntervals
  if (dataTimeInterval === TimeInterval.MONTH) {
    metricIndex += 2;
  } else {
    metricIndex += 3;
  }

  if (utilizeAmortizedCost) {
    const amortizedMetricsOffset = 3; // number of metrics is 4, but offset is 3
    return metricIndex + amortizedMetricsOffset + Number(isCSP);
  }

  return metricIndex;
};

export const getAllDatesFromData = (
  rows: BudgetForecastRowsData | BudgetRows,
  timeInterval: TimeInterval,
  indexValue: number
): string[] => {
  if (!rows) {
    return [];
  }

  return Object.values(rows as Record<string, BudgetRow>).map((row: BudgetRow) =>
    getBudgetRowDate(row, indexValue, timeInterval)
  );
};

export const getDataRowsValues = (
  rows: BudgetRows | BudgetForecastRowsData,
  dataTimeInterval: TimeInterval,
  rowFirstDateIdx: number,
  utilizeAmortizedCost: boolean,
  isCSP: boolean
): Array<{ y: number }> => {
  if (!rows) {
    return [];
  }

  const metricIndex = getRowMetricIndex(rowFirstDateIdx, dataTimeInterval, utilizeAmortizedCost, isCSP);

  const rowValues = Object.values(rows as Record<string, BudgetRow>)
    .map((row) => row[metricIndex])
    .filter((value): value is number | string => value !== null)
    .map((value) => ({ y: Number(value) }));

  return rowValues;
};

export const calculateCumulativeValues = (
  values: BudgetChartData[],
  cumulativeStart?: number
): Array<{ y: number }> => {
  let cumulativeSum = cumulativeStart ?? 0;

  return values.map(({ y }) => {
    cumulativeSum += y ?? 0;
    return { y: cumulativeSum };
  });
};

export const getBudgetRowsDates = (
  rows: BudgetRows,
  forecastRows: BudgetForecastRowsData,
  dataTimeInterval: TimeInterval
): string[] => {
  const rowsDates = getAllDatesFromData(rows, dataTimeInterval, 0);
  const forecastDates = getAllDatesFromData(forecastRows, dataTimeInterval, 1);

  const allDates = [...rowsDates, ...forecastDates];

  return allDates;
};

export const getCumulativeRowsValues = (rows: BudgetChartData[]): Array<{ y: number }> =>
  calculateCumulativeValues(rows);

export const getCumulativeForecastedRowsValues = (
  forecastRows: BudgetChartData[],
  cumulativeStart: number
): Array<{ y: number }> => calculateCumulativeValues(forecastRows, cumulativeStart);

export const getAlertTriggers = (
  data: Array<{ x?: string | null; y?: number | null }>,
  alerts: BudgetAlert[]
): MarkPointData[] => {
  if (!alerts.length || !data.length) {
    return [];
  }

  const validAmounts = data.map((item) => item.y).filter((y) => typeof y === "number");

  if (!validAmounts.length) {
    return [];
  }

  const triggers = alerts
    .filter((alert) => alert.percentage !== 100)
    .map((alert) => {
      const budgetAmountForTrigger = getNearestValue(validAmounts, alert.amount ?? 0);

      const budgetDateIndex = data.findIndex((item) => item.y === budgetAmountForTrigger);

      if (budgetDateIndex === -1 || data.length - 1 === budgetDateIndex) {
        return null;
      }

      const res: MarkPointData = {
        coord: [budgetDateIndex, budgetAmountForTrigger],
        value: budgetTxt.THRESHOLD_TRIGGER,
      };

      return res;
    })
    .filter((trigger) => trigger !== null);

  return triggers;
};

export const getBudgetChartTooltip = (
  params: (CallbackDataParams & { axisValueLabel: string })[],
  theme: Theme,
  valueFormatter: (value: number, isCurrency: boolean) => string,
  isCumulative: boolean = false
): string => {
  const presentPoints = params.map((i) => i.seriesName);
  const actual = params.find((i) => i.seriesName === budgetTxt.ACTUAL_SPEND);
  const forecast = params.find((i) => i.seriesName === budgetTxt.FORECAST);
  const budget = params.find((i) => i.seriesName === budgetTxt.BUDGET_AMOUNT_TO_DATE);

  const isLightMode = theme.palette.mode === ThemeModes.LIGHT;
  const budgetAmountToDateColor = generateHighchartList3(isLightMode ? "light" : "dark")[4];
  const actualColor = generateHighchartList3(isLightMode ? "light" : "dark")[0];
  const forecastColor = isLightMode
    ? lighten(theme.palette.text.disabled, 0.2)
    : darken(theme.palette.text.disabled, 0.9);

  const showForecast = !presentPoints.includes(budgetTxt.ACTUAL_SPEND);
  const currentVariance = showForecast
    ? Number(params[0]?.value ?? 0) - Number(params[1]?.value ?? 0)
    : Number(params[2]?.value ?? 0) - Number(params[0]?.value ?? 0);

  const firstLineColor = showForecast ? forecastColor : actualColor;
  const firstPointName = showForecast ? budgetTxt.FORECASTED : budgetTxt.ACTUALS;
  const firstPointValue = showForecast ? Number(forecast?.value) || 0 : Number(actual?.value) || 0;
  const firstPointDate = showForecast ? forecast?.axisValueLabel : actual?.axisValueLabel;

  const commonTooltipDetails = `
    <div style="font-size:13px;margin-bottom:9px;width:240px;">${firstPointDate}</div>
    <div style="width:100%;height:1px;background-color:#C0C0C0;margin:9px 0 9px 0;"></div>
    <span style="color:${firstLineColor};">
      <b> ${firstPointName}:</b>
    </span>
    <span style="color:rgba(0, 0, 0, 0.60)">${valueFormatter(Number(firstPointValue), false)}</span><br/>
  `;
  const budgetAmountToDateTooltipDetails = !showForecast
    ? `
    <span style="color:${budgetAmountToDateColor}">
      <b> Budget amount to date:</b>
    </span>
     <span style="color:rgba(0, 0, 0, 0.60)">${valueFormatter(Number(budget?.value) || 0, false)}</span><br/>
`
    : "";
  if (!isCumulative) {
    return commonTooltipDetails;
  }

  return `
    ${commonTooltipDetails}
    ${budgetAmountToDateTooltipDetails}
    <span><b> Variance:</b></span>
    <span style="color:rgba(0, 0, 0, 0.60)">${valueFormatter(Number(currentVariance) || 0, false)}</span>
  `;
};

export const getYExtendedMax = (instance: EChartsInstance): number => {
  const yAxisModel = instance.getModel().getComponent("yAxis", 0);
  const scale = yAxisModel.axis.scale;
  const maxYValue = Number(scale.getExtent()[1]);
  const yInterval = Number(scale.getInterval());
  return maxYValue + yInterval;
};

export function getFirstDayOfISOWeek(year, weekNumber) {
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const dayOfWeek = jan4.getUTCDay() || 7;
  const firstMonday = new Date(jan4);
  firstMonday.setUTCDate(jan4.getUTCDate() + 1 - dayOfWeek);

  const startOfWeek = new Date(firstMonday);
  startOfWeek.setUTCDate(firstMonday.getUTCDate() + (weekNumber - 1) * 7);

  return startOfWeek;
}

export function convertToUTCTimestamp(dateString) {
  const weekYearWithMonthDayMatch = dateString.match(/^(\d{4})-W(\d{2}) \((\w{3}) (\d{2})\)$/);
  const weekYearMatch = dateString.match(/^(\d{4}) W(\d{2}) \((\w{3}) (\d{2})\)$/);

  if (weekYearWithMonthDayMatch) {
    const year = parseInt(weekYearWithMonthDayMatch[1], 10);
    const weekNumber = parseInt(weekYearWithMonthDayMatch[2], 10);
    const startOfWeek = DateTime.utc().set({ year }).set({ weekNumber, weekday: 1 }).startOf("day");
    return startOfWeek.toMillis();
  } else if (weekYearMatch) {
    const year = parseInt(weekYearMatch[1], 10);
    const weekNumber = parseInt(weekYearMatch[2], 10);
    const startOfWeek = DateTime.utc().set({ year }).set({ weekNumber, weekday: 1 }).startOf("day");
    return startOfWeek.toMillis();
  } else if (dateString.length === 16) {
    return DateTime.fromFormat(dateString, "yyyy-MM-dd-HH:mm", { zone: "utc" }).toMillis();
  } else if (dateString.length === 10) {
    return DateTime.fromFormat(dateString, "yyyy-MM-dd", { zone: "utc" }).toMillis();
  } else if (dateString.length === 7) {
    return DateTime.fromFormat(dateString, "yyyy-MM", { zone: "utc" }).toMillis();
  } else {
    throw new Error("Unsupported date format");
  }
}

export function hoursElapsedSinceStartOfDay() {
  const now = new Date();
  return now.getHours();
}

export function daysElapsedSinceStartOfWeek() {
  const now = new Date();
  const dayOfWeek = now.getUTCDay();
  return dayOfWeek === 0 ? 7 : dayOfWeek;
}

export function daysElapsedSinceStartOfMonth() {
  const today = DateTime.local();

  const startOfMonth = today.startOf("month");

  const daysUntilToday = today.diff(startOfMonth, "days").days + 1;

  return Math.floor(daysUntilToday);
}

export function weeksElapsedSinceStartOfQuarter() {
  const now = DateTime.utc();
  const quarterStartMonth = Math.floor((now.month - 1) / 3) * 3 + 1;
  const quarterStart = DateTime.utc(now.year, quarterStartMonth, 1);
  const elapsedWeeks = Math.floor(now.diff(quarterStart, "weeks").weeks);
  return elapsedWeeks;
}

export function monthsElapsedSinceStartOfYear() {
  const now = new Date();
  const currentMonth = now.getMonth() + 1;
  return currentMonth;
}

export const getBudgetConfiguration = (
  budgetConfig: BudgetConfig
): {
  type: BudgetConfigurations;
  dynamic?: BudgetDynamicConfigurations;
} => {
  const { allowGrowth, usePrevSpend, rollover } = budgetConfig;
  if (rollover?.enabled) {
    return {
      type: BudgetConfigurations.DYNAMIC,
      dynamic: BudgetDynamicConfigurations.NEXT_PERIOD_ROLLOVER,
    };
  } else if (allowGrowth && usePrevSpend) {
    return {
      type: BudgetConfigurations.DYNAMIC,
      dynamic: BudgetDynamicConfigurations.LAST_PERIOD_AND_PERCENTAGE_GROWTH,
    };
  } else if (allowGrowth) {
    return { type: BudgetConfigurations.DYNAMIC, dynamic: BudgetDynamicConfigurations.PERCENTAGE_GROWTH };
  } else if (usePrevSpend) {
    return { type: BudgetConfigurations.DYNAMIC, dynamic: BudgetDynamicConfigurations.LAST_PERIOD };
  } else {
    return { type: BudgetConfigurations.SINGLE_PERIOD };
  }
};

export const formatBudgetValue = (value: number, currency: CurrencyCode): string =>
  formatValueWithCurrency(value, 2, currency, false);

export const getBudgetAmountToDate = (
  budgetAmount: number,
  type: BudgetTypes,
  timeInterval: TimeInterval | undefined,
  startPeriod?: ModelTimestamp,
  endPeriod?: ModelTimestamp
): number => {
  let currentIntervalValue: number;
  let intervalValue: number;

  if (!timeInterval && type === BudgetTypes.FIXED) {
    timeInterval = TimeInterval.MONTH;
    currentIntervalValue = buildRange(
      startPeriod ? DateTime.fromJSDate(startPeriod.toDate()) : DateTime.utc(),
      endPeriod ? DateTime.fromJSDate(endPeriod.toDate()) : DateTime.utc(),
      timeInterval,
      type
    ).length;
    intervalValue = currentIntervalValue;
  } else {
    currentIntervalValue = previewAmountByInterval[timeInterval ?? TimeInterval.MONTH];
    intervalValue = fullPreviewAmountByInterval[timeInterval ?? TimeInterval.MONTH];
  }

  const currentAmount = (budgetAmount / intervalValue) * currentIntervalValue;
  return currentAmount;
};

export const getRolloverLimit = (timeInterval: TimeInterval): number => {
  switch (timeInterval) {
    case TimeInterval.DAY:
      return 90;
    case TimeInterval.WEEK:
      return 52;
    case TimeInterval.MONTH:
      return 12;
    case TimeInterval.QUARTER:
      return 8;
    case TimeInterval.YEAR:
      return 5;
    default:
      throw new Error("Invalid time interval");
  }
};

export const formatIntervalAmount = (amount: number, interval: TimeInterval): string => {
  const base = interval.toLowerCase();
  return `${amount} ${base}${amount !== 1 ? "s" : ""}`;
};
